import {
  FetchQueryOptions,
  QueryClient,
  QueryFunctionContext,
  UseQueryOptions,
  useQuery,
} from "@tanstack/react-query";
import { AxiosError } from "axios";

import httpClient from "helpers/hybridHttpClient";

import type { LaplaceIdentityResponse } from "types/laplace";
import { LAPLACE_QUERY_KEY_PREFIX } from "./constants";

interface LaplaceAccessKeyOptions {
  userId?: number;
}

export const generateLaplaceAccessKey = (options: LaplaceAccessKeyOptions) =>
  [LAPLACE_QUERY_KEY_PREFIX, "identity", options] as const;

type LaplaceAccessKey = ReturnType<typeof generateLaplaceAccessKey>;

export const fetchLaplaceAccess = async ({
  queryKey: [, , { userId }],
  meta: { ssrContext } = {},
}: QueryFunctionContext<LaplaceAccessKey>) => {
  if (userId == null) {
    throw new Error("userId is required");
  }

  const { data } = await httpClient(ssrContext).get<LaplaceIdentityResponse>(
    `/api/user_integrations/laplace/identity/?field=user_id&value=${userId}`,
  );

  return data;
};

type UseLaplaceAccessQueryOptions<TData> = Omit<
  UseQueryOptions<LaplaceIdentityResponse, AxiosError, TData, LaplaceAccessKey>,
  "queryKey" | "queryFn"
> &
  LaplaceAccessKeyOptions;

type FetchLaplaceAccessQueryOptions<TData> = Omit<
  FetchQueryOptions<
    LaplaceIdentityResponse,
    AxiosError,
    TData,
    LaplaceAccessKey,
    unknown
  >,
  "queryKey" | "queryFn"
> &
  LaplaceAccessKeyOptions;

export const useLaplaceAccess = <TData = LaplaceIdentityResponse>({
  userId,
  enabled = true,
  ...options
}: UseLaplaceAccessQueryOptions<TData>) => {
  return useQuery({
    ...options,
    queryKey: generateLaplaceAccessKey({ userId }),
    queryFn: fetchLaplaceAccess,
    enabled: enabled && userId != null,
  });
};

useLaplaceAccess.prefetchQuery = <TData = LaplaceIdentityResponse>(
  queryClient: QueryClient,
  { userId, ...options }: FetchLaplaceAccessQueryOptions<TData>,
) =>
  userId != null
    ? queryClient.prefetchQuery({
        ...options,
        queryKey: generateLaplaceAccessKey({ userId }),
        queryFn: fetchLaplaceAccess,
      })
    : null;
